import {
  Box,
  Flex,
  FormControl,
  FormLabel,
  Input,
  Text,
  Textarea,
  Tooltip,
  VStack,
} from "@chakra-ui/react";
import React, { useRef } from "react";

import { formatPhoneNumberGracefully } from "../../../utils/string";
import { AiInterviewerFragment } from "../../graphql";
import useCurrentUser from "../../hooks/useCurrentUser";
import AiInterviewerSectionTitle from "./AiInterviewerSectionTitle";
import NotificationDisabledAlert from "./NotificationDisabledAlert";
import { useAiInterviewerFormContext } from "./useAiInterviewerFormContext";

// 2 segments × 160 chars. Must stay in sync with INVITATION_SMS_MAX_RESOLVED_LENGTH in
// backend/python/app/brighthire/graphql_main/ai_interviewer/mutation.py
const SMS_MAX_CHARS = 320;
const STOP_SUFFIX = "Reply STOP to opt out.";
// The system auto-appends the STOP suffix on a new paragraph, so we include it
// in the character budget.
const STOP_SUFFIX_FULL = `\n\n${STOP_SUFFIX}`;
// Candidate name length is variable; assume a representative 10 characters for
// the character budget so recruiters don't get surprised by longer names.
const ASSUMED_CANDIDATE_NAME_LENGTH = 10;
// Sample interview short link — representative of what's actually sent. The
// real URLs follow this pattern: https://app.brighthire.ai/s/<id>
const SAMPLE_INTERVIEW_LINK = "https://app.brighthire.ai/s/abc123";
const SAMPLE_CANDIDATE_NAME = "John";

const TEMPLATE_VARIABLES = [
  { key: "{{candidate_name}}", label: "Candidate Name", required: false },
  { key: "{{position_name}}", label: "Job Title", required: false },
  { key: "{{organization_name}}", label: "Organization", required: true },
  { key: "{{interview_link}}", label: "Interview Link", required: true },
] as const;

const TEMPLATE_VAR_REGEX = /(\{\{[^}]+\}\})/g;
const TEMPLATE_VAR_PATTERN = /\{\{[^}]+\}\}/g;
const BLUE_COLOR = "blue.600";
const RED_COLOR = "red.600";
const ALLOWED_TEMPLATE_VAR_KEYS = new Set<string>(
  TEMPLATE_VARIABLES.map((v) => v.key)
);

type VarRange = { start: number; end: number };

const getVarRanges = (text: string): VarRange[] =>
  Array.from(text.matchAll(TEMPLATE_VAR_PATTERN))
    .filter((m) => m.index !== undefined)
    .map((m) => ({
      start: m.index,
      end: m.index + m[0].length,
    }));

/**
 * Snap a selection so no boundary lands strictly inside a template variable.
 * A collapsed caret inside a variable snaps to the variable's right edge.
 * A range that overlaps any part of a variable is extended outward to cover
 * the full variable.
 */
const snapSelection = (
  start: number,
  end: number,
  text: string
): [number, number] =>
  getVarRanges(text).reduce<[number, number]>(
    ([s, e], v) => {
      if (s === e && s > v.start && s < v.end) {
        return [v.end, v.end];
      }
      const newStart = s > v.start && s < v.end ? v.start : s;
      const newEnd = e > v.start && e < v.end ? v.end : e;
      return [newStart, newEnd];
    },
    [start, end]
  );

/**
 * Splits the message into plain-text and template-variable segments. Allowed
 * template variables are rendered in blue; any unknown `{{...}}` tokens are
 * rendered in red to surface typos to the user.
 */
const renderHighlightedMessage = (message: string): React.ReactNode => {
  const parts = message.split(TEMPLATE_VAR_REGEX);
  return parts.map((part, i) => {
    if (TEMPLATE_VAR_REGEX.test(part)) {
      // Reset regex lastIndex after `.test()` for the next iteration
      TEMPLATE_VAR_REGEX.lastIndex = 0;
      const color = ALLOWED_TEMPLATE_VAR_KEYS.has(part)
        ? BLUE_COLOR
        : RED_COLOR;
      return (
        // eslint-disable-next-line react/no-array-index-key
        <Text as="span" key={i} color={color}>
          {part}
        </Text>
      );
    }
    // eslint-disable-next-line react/no-array-index-key
    return <React.Fragment key={i}>{part}</React.Fragment>;
  });
};

/**
 * Resolves the template into a preview string using human-friendly sample values.
 * Used for the Preview panel only.
 */
const resolvePreview = (
  message: string,
  positionName: string,
  orgName: string
): string => {
  return message
    .replace(/\{\{candidate_name\}\}/g, SAMPLE_CANDIDATE_NAME)
    .replace(/\{\{position_name\}\}/g, positionName)
    .replace(/\{\{organization_name\}\}/g, orgName)
    .replace(/\{\{interview_link\}\}/g, SAMPLE_INTERVIEW_LINK);
};

/**
 * Builds the preview as JSX with the sample interview link highlighted in blue.
 */
const renderHighlightedPreview = (previewText: string): React.ReactNode => {
  const parts = previewText.split(SAMPLE_INTERVIEW_LINK);
  return parts.map((part, i) => (
    // eslint-disable-next-line react/no-array-index-key
    <React.Fragment key={i}>
      {part}
      {i < parts.length - 1 && (
        <Text as="span" color={BLUE_COLOR}>
          {SAMPLE_INTERVIEW_LINK}
        </Text>
      )}
    </React.Fragment>
  ));
};

/**
 * Calculates the total SMS character length after resolving template variables
 * with representative values and appending the auto-added STOP suffix.
 *
 * - candidate_name: assumed 10 characters
 * - position_name: actual AI Interviewer name length
 * - organization_name: actual org name length
 * - interview_link: representative short URL length
 */
const calculateResolvedLength = (
  message: string,
  positionName: string,
  orgName: string
): number => {
  const resolved = message
    .replace(
      /\{\{candidate_name\}\}/g,
      "X".repeat(ASSUMED_CANDIDATE_NAME_LENGTH)
    )
    .replace(/\{\{position_name\}\}/g, positionName)
    .replace(/\{\{organization_name\}\}/g, orgName)
    .replace(/\{\{interview_link\}\}/g, SAMPLE_INTERVIEW_LINK);
  return resolved.length + STOP_SUFFIX_FULL.length;
};

type VariablePillProps = {
  label: string;
  required: boolean;
  tooltipLabel: string;
  inMessage: boolean;
  disabled: boolean;
  muted: boolean;
  onClick: () => void;
};

const VariablePill: React.FC<VariablePillProps> = ({
  label,
  required,
  tooltipLabel,
  inMessage,
  disabled,
  muted,
  onClick,
}) => {
  const disabledTooltip = inMessage
    ? `${label} is already included in the message.`
    : undefined;
  const activeTooltip = disabled ? disabledTooltip : tooltipLabel;

  const pill = (
    <Box
      as="button"
      type="button"
      px="9px"
      py="4px"
      borderRadius="28px"
      borderWidth="1px"
      borderColor="gray.200"
      bg="white"
      fontSize="11.67px"
      fontWeight="normal"
      color={inMessage || muted ? "gray.400" : "gray.800"}
      cursor={disabled ? "default" : "pointer"}
      transition="background-color 0.12s ease, border-color 0.12s ease"
      _hover={
        disabled
          ? undefined
          : {
              bg: "blue.50",
              borderColor: "blue.400",
            }
      }
      onClick={disabled ? undefined : onClick}
      aria-disabled={disabled || undefined}
    >
      {label}
      {required && (
        <Text as="span" color={muted ? "gray.400" : "red.500"} ml="1px">
          *
        </Text>
      )}
    </Box>
  );

  if (!activeTooltip) return pill;

  return (
    <Tooltip label={activeTooltip} placement="top" hasArrow openDelay={200}>
      {pill}
    </Tooltip>
  );
};

type AiInterviewerNotificationsInvitationSmsSettingsProps = {
  aiInterviewer: AiInterviewerFragment;
  readOnly: boolean;
};

const AiInterviewerNotificationsInvitationSmsSettings: React.FC<
  AiInterviewerNotificationsInvitationSmsSettingsProps
> = ({ aiInterviewer, readOnly }) => {
  const { register, watch, setValue } = useAiInterviewerFormContext();
  const currentUser = useCurrentUser();
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const overlayRef = useRef<HTMLDivElement | null>(null);

  const message = watch("invitationSmsMessage") ?? "";
  const invitationSmsEnabled = watch("invitationSmsEnabled") ?? false;
  const orgName = currentUser?.organization?.name ?? "";
  const positionName = aiInterviewer.name;
  const isDisabled = readOnly || !invitationSmsEnabled;

  const resolvedLength = calculateResolvedLength(
    message,
    positionName,
    orgName
  );

  const requiredMissing = TEMPLATE_VARIABLES.filter(
    (v) => v.required && !message.includes(v.key)
  );
  const hasRequiredError = requiredMissing.length > 0;

  const previewText = resolvePreview(message, positionName, orgName);

  const commitMessage = (newValue: string): boolean => {
    const newResolved = calculateResolvedLength(
      newValue,
      positionName,
      orgName
    );
    if (newResolved > SMS_MAX_CHARS) return false;
    setValue("invitationSmsMessage", newValue, { shouldDirty: true });
    return true;
  };

  const handleInsertVariable = (varKey: string): void => {
    const textarea = textareaRef.current;
    const start = textarea?.selectionStart ?? message.length;
    const end = textarea?.selectionEnd ?? start;
    const newValue = message.slice(0, start) + varKey + message.slice(end);
    if (!commitMessage(newValue)) return;
    requestAnimationFrame(() => {
      if (!textarea) return;
      textarea.focus();
      const pos = start + varKey.length;
      textarea.setSelectionRange(pos, pos);
    });
  };

  const handleSelect = (e: React.SyntheticEvent<HTMLTextAreaElement>): void => {
    const textarea = e.currentTarget;
    const { selectionStart, selectionEnd, value } = textarea;
    const [newStart, newEnd] = snapSelection(
      selectionStart,
      selectionEnd,
      value
    );
    if (newStart !== selectionStart || newEnd !== selectionEnd) {
      textarea.setSelectionRange(newStart, newEnd);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>): void => {
    const textarea = e.currentTarget;
    const { selectionStart, selectionEnd, value } = textarea;

    // Treat each template variable as a single character when moving the
    // caret with the arrow keys.
    if (
      !e.shiftKey &&
      selectionStart === selectionEnd &&
      (e.key === "ArrowLeft" || e.key === "ArrowRight")
    ) {
      const dir = e.key === "ArrowRight" ? 1 : -1;
      const candidate = selectionStart + dir;
      const containingVar = getVarRanges(value).find(
        (v) => candidate > v.start && candidate < v.end
      );
      if (containingVar) {
        e.preventDefault();
        const newPos = dir > 0 ? containingVar.end : containingVar.start;
        textarea.setSelectionRange(newPos, newPos);
        return;
      }
    }

    if (selectionStart !== selectionEnd) return;

    if (e.key === "Backspace") {
      const before = value.slice(0, selectionStart);
      const match = before.match(/\{\{[^}]+\}\}$/);
      if (match) {
        e.preventDefault();
        const newValue =
          before.slice(0, before.length - match[0].length) +
          value.slice(selectionStart);
        commitMessage(newValue);
        const newPos = selectionStart - match[0].length;
        requestAnimationFrame(() => {
          textarea.setSelectionRange(newPos, newPos);
        });
      }
    } else if (e.key === "Delete") {
      const after = value.slice(selectionStart);
      const match = after.match(/^\{\{[^}]+\}\}/);
      if (match) {
        e.preventDefault();
        const newValue =
          value.slice(0, selectionStart) + after.slice(match[0].length);
        commitMessage(newValue);
        requestAnimationFrame(() => {
          textarea.setSelectionRange(selectionStart, selectionStart);
        });
      }
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>): void => {
    commitMessage(e.target.value);
  };

  const handleScroll = (): void => {
    // Keep the highlight overlay in sync with textarea scroll position.
    if (textareaRef.current && overlayRef.current) {
      overlayRef.current.scrollTop = textareaRef.current.scrollTop;
      overlayRef.current.scrollLeft = textareaRef.current.scrollLeft;
    }
  };

  const messageRegistration = register("invitationSmsMessage");

  // Shared text styles so the overlay and textarea render characters at
  // identical positions.
  const editorTextStyles = {
    fontSize: "14px",
    lineHeight: "20px",
    fontFamily: "var(--chakra-fonts-body)",
    letterSpacing: "normal",
    padding: "8px 12px",
    whiteSpace: "pre-wrap" as const,
    wordBreak: "break-word" as const,
  };

  return (
    <VStack spacing={4} align="stretch" w="100%">
      <Box>
        <AiInterviewerSectionTitle mb={0} title="Invitation SMS" />
        <Text color="gray.600" fontSize="sm" mt={1}>
          Customize the text message sent to candidates requesting them to take
          an AI-moderated interview.
        </Text>
      </Box>

      {!invitationSmsEnabled && (
        <NotificationDisabledAlert notificationName="Invitation SMS" />
      )}

      <FormControl id="smsFromPhoneNumber">
        <FormLabel fontSize="sm" mb={1} color="gray.600">
          From phone number
        </FormLabel>
        <Input
          value={
            aiInterviewer.aiInterviewPhoneNumber
              ? formatPhoneNumberGracefully(
                  aiInterviewer.aiInterviewPhoneNumber
                )
              : ""
          }
          fontSize="sm"
          borderRadius="md"
          isDisabled
          _disabled={{ bg: "gray.50" }}
          readOnly
        />
      </FormControl>

      <Box>
        <Text fontSize="sm" color="gray.600" mb={2}>
          Template Variables (click to add to template):
        </Text>
        <Flex wrap="wrap" gap={2} mb={hasRequiredError && !isDisabled ? 1 : 0}>
          {TEMPLATE_VARIABLES.map((v) => {
            const inMessage = message.includes(v.key);
            return (
              <VariablePill
                key={v.key}
                label={v.label}
                required={v.required}
                tooltipLabel={`Insert ${v.label}`}
                inMessage={inMessage}
                disabled={inMessage || isDisabled}
                muted={isDisabled}
                onClick={() => {
                  if (!inMessage && !isDisabled) handleInsertVariable(v.key);
                }}
              />
            );
          })}
        </Flex>
        {hasRequiredError && !isDisabled && (
          <Text fontSize="xs" color="red.600">
            *Add required variables: Organization Name and Interview Link
          </Text>
        )}
      </Box>

      <FormControl id="smsMessage">
        <FormLabel fontSize="sm" mb={1} color="gray.600">
          Message
        </FormLabel>
        <Tooltip
          label="Enable the Invitation SMS to edit this message"
          isDisabled={!isDisabled || readOnly}
          placement="top"
          hasArrow
        >
          <Box
            position="relative"
            borderWidth="1px"
            borderColor="gray.200"
            borderRadius="md"
            bg={isDisabled ? "gray.50" : "white"}
            minH="88px"
            cursor={isDisabled ? "not-allowed" : undefined}
            _focusWithin={
              isDisabled
                ? undefined
                : {
                    borderColor: "blue.500",
                    boxShadow: "0 0 0 1px var(--chakra-colors-blue-500)",
                  }
            }
          >
            {/* Highlighted mirror of the textarea content */}
            <Box
              ref={overlayRef}
              position="absolute"
              inset={0}
              overflow="hidden"
              color="gray.800"
              pointerEvents="none"
              aria-hidden="true"
              {...editorTextStyles}
            >
              {renderHighlightedMessage(message)}
              {/* Trailing newline alignment fix: if the message ends with \n,
                add a space so the overlay height matches the textarea. */}
              {message.endsWith("\n") ? " " : null}
            </Box>
            <Textarea
              name={messageRegistration.name}
              ref={(el) => {
                textareaRef.current = el;
                messageRegistration.ref(el);
              }}
              value={message}
              onChange={handleChange}
              onBlur={messageRegistration.onBlur}
              onKeyDown={handleKeyDown}
              onSelect={handleSelect}
              onScroll={handleScroll}
              border="none"
              borderRadius="md"
              bg="transparent"
              color="transparent"
              sx={{
                caretColor: "var(--chakra-colors-gray-800)",
                "&::selection": {
                  // Translucent so the highlighted-overlay text below shows through.
                  backgroundColor: "rgba(66, 153, 225, 0.3)",
                },
              }}
              _focus={{ boxShadow: "none", border: "none" }}
              resize={isDisabled ? "none" : "vertical"}
              minH="88px"
              position="relative"
              readOnly={isDisabled}
              tabIndex={isDisabled ? -1 : undefined}
              pointerEvents={isDisabled ? "none" : undefined}
              {...editorTextStyles}
            />
          </Box>
        </Tooltip>
        <Text fontSize="xs" color="gray.500" textAlign="right" mt={1}>
          {resolvedLength} / {SMS_MAX_CHARS} characters
        </Text>
      </FormControl>

      <FormControl id="smsPreview">
        <FormLabel fontSize="sm" mb={1} color="gray.600">
          Preview
        </FormLabel>
        <Box
          bg="gray.50"
          borderRadius="md"
          px={3}
          py={2}
          fontSize="sm"
          color="gray.800"
          whiteSpace="pre-wrap"
          minH="88px"
        >
          {renderHighlightedPreview(previewText)}
          {"\n\n"}
          {STOP_SUFFIX}
        </Box>
      </FormControl>
    </VStack>
  );
};

export default AiInterviewerNotificationsInvitationSmsSettings;
